import React from "react";

/**
 * Iter 143 — Viewer-level ErrorBoundary.
 *
 * Real MASCI iPad field test showed the plan viewer black-screening the
 * entire app on a runtime exception (suspect: touch handler null deref
 * or canvas/image dimension crash on iPad Safari). This boundary
 * guarantees the app NEVER full-blacks: any error inside the viewer
 * shows a recoverable error card with the actual error message + actions.
 *
 * Logs the error to console with a clear prefix so the operator can
 * grab it from Safari's Web Inspector or from forwarded sentry/logs.
 */
export class ViewerErrorBoundary extends React.Component {
  state = { error: null, info: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log loudly so a copy/paste from Safari Web Inspector is enough
    // to debug the next iPad crash.
    // eslint-disable-next-line no-console
    console.error("[ViewerErrorBoundary] crash:", error, info);
    this.setState({ info });
  }

  handleReloadSheet = () => {
    // Force a fresh viewer mount without losing the user's URL.
    this.setState({ error: null, info: null });
    // Bump a query param to force any in-progress fetch caches to drop.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("_recover", String(Date.now()));
      window.history.replaceState({}, "", url.toString());
      window.location.reload();
    } catch {
      window.location.reload();
    }
  };

  handleBackToProject = () => {
    // Best-effort: strip /sheets/... segment.
    try {
      const path = window.location.pathname.replace(
        /\/sheets\/[^/]+.*$/, "",
      );
      window.location.assign(path || "/");
    } catch {
      window.location.assign("/");
    }
  };

  handleCopyDiagnostics = async () => {
    const err = this.state.error;
    const payload = {
      message: err?.message || String(err),
      stack: err?.stack || null,
      componentStack: this.state.info?.componentStack || null,
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: { w: window.innerWidth, h: window.innerHeight },
      time: new Date().toISOString(),
    };
    const text = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2500);
    } catch {
      // iPad sometimes blocks clipboard outside user gesture timing;
      // fall back to a visible textarea select-all hint.
      this.setState({ copied: false, clipboardFallback: text });
    }
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const msg = error?.message || String(error);

    return (
      <div
        className="min-h-screen w-full flex items-center justify-center p-6 bg-[var(--fop-bg,#0b0f14)] text-[var(--fop-text,#e5e7eb)]"
        data-testid="viewer-error-boundary"
        role="alert"
      >
        <div className="max-w-xl w-full border border-[var(--fop-border,#1f2937)] rounded-lg p-6 bg-[var(--fop-surface,#0f1620)]">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-400 mb-2">
            Plan viewer hit an error
          </div>
          <h2 className="text-xl font-semibold mb-2" data-testid="viewer-error-title">
            We caught a crash before the screen went black.
          </h2>
          <p className="text-sm text-[var(--fop-text-2,#9ca3af)] mb-3">
            The sheet viewer threw an unrecoverable error. Your data is safe.
            Reload the sheet to try again, or go back to the project.
          </p>
          <pre
            className="text-[11px] font-mono whitespace-pre-wrap break-all bg-black/40 border border-[var(--fop-border,#1f2937)] rounded p-2 mb-4 max-h-48 overflow-auto"
            data-testid="viewer-error-message"
          >
            {msg}
          </pre>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={this.handleReloadSheet}
              className="px-3 py-2 rounded bg-[var(--fop-primary,#2563eb)] text-white text-sm font-medium"
              data-testid="viewer-error-reload-btn"
            >
              Reload sheet
            </button>
            <button
              type="button"
              onClick={this.handleBackToProject}
              className="px-3 py-2 rounded border border-[var(--fop-border-hi,#374151)] text-sm"
              data-testid="viewer-error-back-btn"
            >
              Back to project
            </button>
            <button
              type="button"
              onClick={this.handleCopyDiagnostics}
              className="px-3 py-2 rounded border border-[var(--fop-border-hi,#374151)] text-sm"
              data-testid="viewer-error-copy-btn"
            >
              {this.state.copied ? "Copied" : "Copy diagnostics"}
            </button>
          </div>

          {this.state.clipboardFallback && (
            <textarea
              readOnly
              className="mt-3 w-full h-32 text-[10px] font-mono bg-black/40 border border-[var(--fop-border,#1f2937)] rounded p-2"
              value={this.state.clipboardFallback}
              onFocus={(e) => e.target.select()}
              data-testid="viewer-error-clipboard-fallback"
            />
          )}
        </div>
      </div>
    );
  }
}

export default ViewerErrorBoundary;
